<template>
  <div>

    <!-- TODO: if the location is not found, then display a message saying so -->

    <v-form @submit.prevent="submit" novalidate>

      <v-row>

        <v-col cols="12" lg="6" class="order-lg-1">

          <v-card variant="tonal" class="mb-6 h-100">
            <v-card-text>

              <!-- Organization -->

              <template v-if="canManageOrganizations">

                <o-autocomplete
                  v-if="!isExistingLocation && $can('locations.write')"
                  :label="$t('titles.organization')"
                  v-model.number="v$.organizationID.$model"
                  :validator="v$.organizationID"
                  :items="organizationOptions"
                  required
                />

                <o-text-field
                  v-if="isExistingLocation || !$can('locations.write')"
                  :label="$t('titles.organization')"
                  :model-value="organization ? organization.name : '(unknown)'"
                  readonly
                />

              </template>

              <!-- Address -->

              <o-autocomplete
                v-if="!isExistingLocation"
                :label="$t('titles.address')"
                v-model="v$.facilityID.$model"
                v-model:search="v$.facilityLookupSubstring.$model"
                return-object
                :loading="facilityLookupStatus.isBusy()"
                :items="matchingFacilities"
                hide-no-data
                :error-messages="v$.facilityLookupSubstring.$errors.map(e => e.$message)"
                required
              />

              <o-text-field
                v-if="isExistingLocation"
                :label="$t('titles.address')"
                readonly
                :model-value="props.location.address"
              />

              <!-- Name -->

              <o-text-field
                v-if="canWriteLocations"
                :label="$t('titles.name')"
                v-model="v$.locationName.$model"
                :validator="v$.locationName"
                required
              />

              <o-text-field
                v-if="!canWriteLocations"
                :label="$t('titles.name')"
                readonly
                :model-value="v$.locationName.$model"
              />

              <v-row>

                <!-- Total Parking Assets Stalls -->
                <v-col cols="6" md="3">

                  <o-text-field
                    v-if="canWriteLocations"
                    :label="$t('titles.total_parking_spaces')"
                    type="number"
                    v-model.number="v$.totalStalls.$model"
                    :validator="v$.totalStalls"
                    required
                  />

                  <o-text-field
                    v-if="!canWriteLocations"
                    :label="$t('titles.total_parking_spaces')"
                    readonly
                    :model-value="v$.totalStalls.$model"
                  />

                </v-col>

                <!-- Total Allocation (Online Stalls) -->
                <v-col cols="6" md="3">

                  <o-text-field
                    v-if="canWriteLocations"
                    :label="$t('titles.online_allocation')"
                    type="number"
                    v-model.number="v$.onlineStalls.$model"
                    :validator="v$.onlineStalls"
                    required
                  />

                  <o-text-field
                    v-if="!canWriteLocations"
                    :label="$t('titles.online_allocation')"
                    readonly
                    :model-value="v$.onlineStalls.$model"
                  />

                </v-col>

                <v-col cols="6" md="3">
                  <o-select
                    :label="$t('titles.status')"
                    v-model="formState.status"
                    :items="[
                      {value: 'onboarded', title: $t('enums.location_statuses.onboarded')},
                      {value: 'live', title: $t('enums.location_statuses.live')},
                      {value: 'archived', title: $t('enums.location_statuses.archived')},
                    ]"
                    :readonly="!canWriteLocations"
                  />
                </v-col>

                <v-col cols="6" md="3">
                  <o-switch
                    v-if="canWriteLocations"
                    :label="$t('titles.gated_barrier')"
                    v-model="formState.gatedBarrier"
                    :readonly="!canWriteLocations"
                  />
                </v-col>

              </v-row>

            </v-card-text>
          </v-card>

        </v-col>

        <v-col cols="12" lg="6" class="order-lg-3">

          <v-card v-if="canReadInternalNotes" variant="tonal" :title="$t('titles.internal_notes')" class="mb-6">

            <v-card-text>
              
              <o-markdown-field
                v-model="formState.internalNotes"
                :readonly="!canWriteInternalNotes"
              />

            </v-card-text>

          </v-card>

        </v-col>

        <v-col cols="12" lg="6" class="order-lg-2">

          <v-card :title="$t('titles.hours')" variant="tonal" class="mb-6 h-100">
            <v-card-text>

              <v-table>
                <tbody>
                  <tr v-for="row, weekday in formState.locationHours">
                    <th scope="row">{{ $t(`enums.weekdays.${weekday}`) }}</th>
                    <td>
                      <template v-if="row.mode === 'closed'">
                        {{ $t('titles.closed') }}
                      </template>
                      <template v-if="row.mode === 'open24hours'">
                        {{ $t('titles.open_24_hours') }}
                      </template>
                      <template v-if="row.mode === 'open'">
                        {{ formatTime(row.openLocal) }} – {{ formatTime(row.closeLocal) }}
                      </template>
                    </td>
                    <td v-if="canWriteLocations" class="text-end">
                      <v-btn icon density="compact" @click="openHoursModal(weekday, row.mode, row.openLocal, row.closeLocal)">
                        <v-icon icon="$edit" size="compact" />
                        <v-tooltip activator="parent" :text="$t('actions.edit')" />
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <p v-if="hoursDirty" class="text-warning mt-1">
                Hours have been changed. Don't forget to save!
              </p>

            </v-card-text>
          </v-card>

        </v-col>

      </v-row>

      <v-btn
        v-if="canWriteLocations"
        type="submit"
        prepend-icon="$confirm"
        :loading="submitting"
        :disabled="submitting"
      >{{ $t('actions.save') }}</v-btn>

    </v-form>

  </div>
</template>

<script setup>

import { computed, inject, onBeforeMount, reactive, ref, watch } from 'vue'
import { useTitle } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// components
import LocationHoursModal from './LocationHoursModal.vue'
import OMarkdownField from '../../forms/OMarkdownField.vue'

// models
import Facility from '../../../models/Facility'
import Organization from '../../../models/Organization'
import Location from '../../../models/Location'
import LocationHours from '../../../models/LocationHours'

// validation
import useVuelidate from '@vuelidate/core'
import { required, minValue } from '../../../utils/i18n-validators'
import { helpers } from '@vuelidate/validators'

// misc
import _ from 'underscore'
import asyncStatus from '../../../utils/asyncStatus'
import NotificationService from '../../../services/NotificationService'
import { openDialog } from 'vue3-promise-dialog'

const props = defineProps({

  location: {
    type: Location,
    required: true,
  },

})

const router = useRouter()
const { t } = useI18n()

const $can = inject('$can')
const canManageOrganizations = $can('organizations.manage')
const canWriteLocations = $can('locations.write')
const canReadInternalNotes = $can('locationInternalNotes.read')
const canWriteInternalNotes = $can('locationInternalNotes.write')

const isExistingLocation = !!props.location.ID

const title = useTitle()
title.value = isExistingLocation ? props.location.locationName : t('titles.create_location')

// If we're viewing/editing an existing location, then load the associated org, so we can display it
let organization
if (isExistingLocation) {
  organization = Organization.find(props.location.organizationID)
}

/**
 * Dropdown options
 */

const organizationOptions = computed(() => {
  return Organization.getOptions(true)
})

/**
 * Form state and rules
 */

const facilityLookupStatus = asyncStatus()

const formState = reactive({
  organizationID: props.location.organizationID,
  facilityID: null, // this field is only sent to the API when creating a new location, so there's no need to populate it when editing an existing location
  facilityLookupSubstring: '',
  locationName: props.location.locationName,
  totalStalls: props.location.totalStalls,
  onlineStalls: props.location.onlineStalls,
  gatedBarrier: props.location.gatedBarrier,
  status: props.location.status,
  internalNotes: props.location.internalNotes,
  locationHours: {
    sun: {originalID: null, mode: 'open24hours', openLocal: null, closeLocal: null},
    mon: {originalID: null, mode: 'open24hours', openLocal: null, closeLocal: null},
    tue: {originalID: null, mode: 'open24hours', openLocal: null, closeLocal: null},
    wed: {originalID: null, mode: 'open24hours', openLocal: null, closeLocal: null},
    thu: {originalID: null, mode: 'open24hours', openLocal: null, closeLocal: null},
    fri: {originalID: null, mode: 'open24hours', openLocal: null, closeLocal: null},
    sat: {originalID: null, mode: 'open24hours', openLocal: null, closeLocal: null},
  },
})

const notGreaterThanTotalStalls = (value) => {
  return !helpers.req(value) || value <= formState.totalStalls
}

const lookupStatusDoesNotHaveError = (value) => {
  return !helpers.req(value) || !facilityLookupStatus.isError()
}

const formRules = computed(() => {

  const rules = {
    organizationID: {
      required,
    },
    locationName: {
      required,
    },
    totalStalls: {
      required,
      minValue: minValue(0),
    },
    onlineStalls: {
      required,
      minValue: minValue(0),
      notGreaterThanTotalStalls: helpers.withMessage(
        'Cannot be greater than Total Parking Assets Stalls',
        notGreaterThanTotalStalls
      ),
    },
  }

  // facilityID is only required if we're creating a new location
  if (!isExistingLocation) {
    rules.facilityID = { required }
    rules.facilityLookupSubstring = {
      lookupStatusDoesNotHaveError: helpers.withMessage(
        'Couldn\'t look up facilities!',
        lookupStatusDoesNotHaveError
      )
    }
  }
  
  return rules

})

const v$ = useVuelidate(formRules, formState)

onBeforeMount(() => {
  populateFormLocationHours()
})

function populateFormLocationHours() {

  // reset to default
  Object.values(formState.locationHours).forEach((formRow) => {
    formRow.originalID = null
    formRow.mode = 'open24hours'
    formRow.openLocal = null
    formRow.closeLocal = null
  })

  const locationHours = LocationHours.query().where('locationID', props.location.ID).get()

  locationHours.forEach((locationHour) => {

    const formRow = formState.locationHours[locationHour.dayOfWeek]

    if (!formRow) {
      return
    }

    formRow.originalID = locationHour.ID

    if (locationHour.openLocal === null && locationHour.closeLocal === null) {
      formRow.mode = 'closed'
    } else {
      formRow.mode = 'open'
      formRow.openLocal = locationHour.openLocal
      formRow.closeLocal = locationHour.closeLocal
    }

  })

}

/**
 * Facility/address lookup
 */

const matchingFacilities = ref([])

watch(() => formState.facilityLookupSubstring, (newSubstring) => {

  if (newSubstring.length < 3) {
    matchingFacilities.value = []
    return
  }

  // If the field value was changed because the user just clicked on a match,
  // don't issue a new lookup. 
  if (formState.facilityID && newSubstring === formState.facilityID.title) {
    return
  }

  getMatchingFacilities(newSubstring)

})

// Use Underscore's debounce utility to prevent this API call from being
// triggered excessively.
const getMatchingFacilities = _.debounce((substring) => {

  facilityLookupStatus.setBusy()

  Facility.lookUpByAddress(substring)
    .then((result) => {
      matchingFacilities.value = result.response.data.addresses.map((address) => {
        return {
          value: address.facilityID,
          title: address.normalizedAddress,
        }
      })
      facilityLookupStatus.setReady()
    })
    .catch((error) => {
      console.error(error)
      facilityLookupStatus.setError(t('alerts.couldnt_load_facilities'))
    })

}, 500)

/**
 * Location Hours
 */

const hoursDirty = ref(false)

function openHoursModal(weekday, mode, openLocal, closeLocal) {
  openDialog(LocationHoursModal, {
    weekdays: [weekday],
    mode: mode,
    openLocal: openLocal,
    closeLocal: closeLocal,
  }).then((result) => {
    if (!result) {      
      return // modal was canceled
    }
    result.weekdays.forEach((weekday) => {
      formState.locationHours[weekday].mode = result.mode
      formState.locationHours[weekday].openLocal = result.openLocal
      formState.locationHours[weekday].closeLocal = result.closeLocal
    })
    hoursDirty.value = true
  })
}

/**
 * Submission
 */

const submitting = ref(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid || !canWriteLocations) {
    return
  }

  submitting.value = true

  let promise, payload

  if (isExistingLocation) {
    payload = buildPayload(false)
    promise = Location.saveExisting(payload)
  } else {
    payload = buildPayload(true)
    promise = Location.saveNew(payload)
  }

  promise
    .then((result) => {
      populateFormLocationHours()
      hoursDirty.value = false
      if (isExistingLocation) {
        NotificationService.success(t('alerts.location_updated'))
      } else {
        NotificationService.success(t('alerts.location_created'))
        const locationID = result.response.data.ID
        router.push({
          name: 'locationDetail', params: { locationID: locationID }
        })
      }
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_save_location'))
    })
    .finally(() => {
      submitting.value = false
    })

}

function buildPayload(create = false) {

  let locationHoursPayload

  if (create) {

    locationHoursPayload = Object.entries(formState.locationHours)
      .filter(([weekday, formRow]) => {
        return formRow.mode !== 'open24hours'
      })
      .map(([weekday, formRow]) => {

        switch (formRow.mode) {

          case 'open':
            return {
              dayOfWeek: weekday,
              openLocal: formRow.openLocal,
              closeLocal: formRow.closeLocal,
            }
            break

          case 'closed':
            return {
              dayOfWeek: weekday,
              openLocal: null,
              closeLocal: null,
            }
            break

        }

      })

  } else {

    locationHoursPayload = {create: [], update: [], delete: []}

    Object.entries(formState.locationHours).forEach(([weekday, formRow]) => {

      if (formRow.mode === 'open24hours') {
        if (formRow.originalID) {
          locationHoursPayload.delete.push(formRow.originalID)
        }
      }

      if (formRow.mode === 'open') {
        if (formRow.originalID) {
          locationHoursPayload.update.push({
            ID: formRow.originalID,
            openLocal: formRow.openLocal,
            closeLocal: formRow.closeLocal,
          })
        } else {
          locationHoursPayload.create.push({
            dayOfWeek: weekday,
            openLocal: formRow.openLocal,
            closeLocal: formRow.closeLocal,
          })
        }
      }

      if (formRow.mode === 'closed') {
        if (formRow.originalID) {
          locationHoursPayload.update.push({
            ID: formRow.originalID,
            openLocal: null,
            closeLocal: null,
          })
        } else {
          locationHoursPayload.create.push({
            dayOfWeek: weekday,
            openLocal: null,
            closeLocal: null,
          })
        }
      }

    })

  }

  const payload = {
    locationName: formState.locationName,
    totalStalls: formState.totalStalls,
    onlineStalls: formState.onlineStalls,
    gatedBarrier: formState.gatedBarrier,
    status: formState.status,
    locationHours: locationHoursPayload,
    internalNotes: formState.internalNotes === null ? null : formState.internalNotes.trim(),
  }

  if (isExistingLocation) {
    payload.ID = props.location.ID
  } else {
    payload.facilityID = formState.facilityID.value
    payload.organizationID = formState.organizationID
  }

  return payload

}

/**
 * Misc
 */

function formatTime(time) {

  const [hourStr, minuteStr] = time.split(':')
  const hour = parseInt(hourStr, 10)
  let adjustedHour

  if (hour === 0) {
    adjustedHour = 12
  } else if (hour >= 1 && hour <= 12) {
    adjustedHour = hour
  } else {
    adjustedHour = hour - 12
  }

  const meridiem = hour < 12 ? 'am' : 'pm'

  return `${adjustedHour}:${minuteStr} ${meridiem}`

}

</script>
